import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, addDays, startOfWeek, isSameDay } from 'date-fns';
import { Package, Clock, Factory, CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export default function MobileSchedulePage() {
  const navigate = useNavigate();
  const [workOrders, setWorkOrders] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [showCalendar, setShowCalendar] = useState(false);

  const fetchWorkOrders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/work-orders`);
      setWorkOrders(res.data || []);
    } catch (err) {
      console.error('Failed to fetch WOs:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWorkOrders();
  }, [fetchWorkOrders]);

  // Generate 14 days from start of current week
  const weekStart = startOfWeek(selectedDate, { weekStartsOn: 0 });
  const days = Array.from({ length: 14 }, (_, i) => addDays(weekStart, i));

  const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
  const dayWOs = workOrders.filter(wo => wo.planned_date === selectedDateStr);

  // Check which days have WOs for dot indicators
  const datesWithWOs = new Set(workOrders.map(wo => wo.planned_date));
  
  // Dates that have WOs (for calendar highlighting)
  const woDateObjects = workOrders
    .map(wo => {
      try { return new Date(wo.planned_date + 'T00:00:00'); } catch { return null; }
    })
    .filter(Boolean);

  const statusLabel = (s) => {
    const map = { scheduled: 'Scheduled', staged: 'Staged', in_production: 'Running', completed: 'Done', finalized: 'Done' };
    return map[s] || s;
  };

  const handleCalendarSelect = (date) => {
    if (date) {
      setSelectedDate(date);
      setShowCalendar(false);
    }
  };

  if (loading) {
    return (
      <div className="mobile-page">
        <div className="mobile-loading"><div className="mobile-spinner" /></div>
      </div>
    );
  }

  return (
    <div className="mobile-page">
      <div className="mobile-page-header">
        <div style={{ flex: 1 }}>
          <div className="mobile-page-title">Schedule</div>
          <div className="mobile-page-subtitle">{format(selectedDate, 'MMMM yyyy')}</div>
        </div>
        <button
          onClick={() => setShowCalendar(!showCalendar)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
            background: showCalendar ? '#007aff' : 'white', color: showCalendar ? 'white' : '#007aff',
            border: '1px solid #007aff', borderRadius: 10, fontSize: 14, fontWeight: 500, cursor: 'pointer'
          }}
        >
          <CalendarDays size={16} />
          {showCalendar ? 'Close' : 'Pick Date'}
        </button>
      </div>

      {/* Calendar Picker */}
      {showCalendar && (
        <div className="mobile-detail-section" style={{ marginBottom: 16, padding: 8 }}>
          <DayPicker
            mode="single"
            selected={selectedDate}
            onSelect={handleCalendarSelect}
            modifiers={{ hasWO: woDateObjects }}
            modifiersStyles={{
              hasWO: { fontWeight: 700, color: '#007aff' },
            }}
            styles={{
              caption: { color: '#1c1c1e' },
              day: { fontSize: 15 },
              root: { width: '100%', margin: 0 },
            }}
            footer={
              <div style={{ textAlign: 'center', fontSize: 12, color: '#8e8e93', marginTop: 8 }}>
                Bold dates have work orders
              </div>
            }
          />
        </div>
      )}

      {/* Day Scroller */}
      <div className="mobile-day-scroller">
        {days.map((day) => {
          const isActive = isSameDay(day, selectedDate);
          const isToday = isSameDay(day, new Date());
          const hasWOs = datesWithWOs.has(format(day, 'yyyy-MM-dd'));
          return (
            <button
              key={day.toISOString()}
              className={`mobile-day-pill ${isActive ? 'active' : ''} ${isToday ? 'today' : ''}`}
              onClick={() => setSelectedDate(day)}
            >
              <span className="mobile-day-pill-day">{format(day, 'EEE')}</span>
              <span className="mobile-day-pill-date">{format(day, 'd')}</span>
              {hasWOs && <div className="mobile-day-pill-dot" />}
            </button>
          );
        })}
      </div>

      {/* Today button */}
      {!isSameDay(selectedDate, new Date()) && (
        <button
          onClick={() => setSelectedDate(new Date())}
          style={{ display: 'block', margin: '0 auto 12px', background: 'none', border: 'none', color: '#007aff', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
        >
          Jump to Today
        </button>
      )}

      {/* Selected Day Header */}
      <div className="mobile-section-header">
        <span className="mobile-section-title">{format(selectedDate, 'EEEE, MMM d')}</span>
        <span style={{ fontSize: 13, color: '#8e8e93' }}>{dayWOs.length} work order{dayWOs.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Work Orders for Selected Day */}
      {dayWOs.length === 0 ? (
        <div className="mobile-empty-state">
          <Factory />
          <p>No work orders scheduled</p>
        </div>
      ) : (
        dayWOs.map(wo => {
          const progress = wo.planned_quantity > 0 ? ((wo.actual_quantity || 0) / wo.planned_quantity) * 100 : 0;
          const showProgress = wo.status === 'in_production' || wo.status === 'completed' || wo.status === 'finalized';
          return (
            <div
              key={wo.wo_id}
              className={`mobile-wo-card status-${wo.status}`}
              onClick={() => navigate(`/mobile/production/${wo.wo_id}`)}
            >
              <div className="mobile-wo-header">
                <div>
                  <div className="mobile-wo-number">{wo.wo_number} · Line {wo.production_line}</div>
                  <div className="mobile-wo-item-name">{wo.item_name}</div>
                </div>
                <span className={`mobile-status-badge ${wo.status}`}>
                  {statusLabel(wo.status)}
                </span>
              </div>
              <div className="mobile-wo-meta">
                <span className="mobile-wo-meta-item">
                  <Clock size={14} />
                  {wo.planned_start_time || 'TBD'} - {wo.planned_end_time || 'TBD'}
                </span>
                <span className="mobile-wo-meta-item">
                  <Package size={14} />
                  {showProgress ? `${wo.actual_quantity || 0} / ` : ''}{wo.planned_quantity} cases
                </span>
              </div>
              {showProgress && (
                <div className="mobile-wo-progress">
                  <div className="mobile-wo-progress-bar">
                    <div
                      className={`mobile-wo-progress-fill ${progress >= 100 ? 'green' : progress > 50 ? 'blue' : 'orange'}`}
                      style={{ width: `${Math.min(progress, 100)}%` }}
                    />
                  </div>
                  <div className="mobile-wo-progress-text">
                    <span>{progress.toFixed(0)}%</span>
                    <span>{wo.actual_quantity || 0} / {wo.planned_quantity}</span>
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
